/**
 * Session Manager Service
 * ============================================================================
 *
 * Purpose:
 * --------
 * Manages authentication sessions with proactive token refresh.
 * Ensures tokens are refreshed BEFORE they expire to prevent 401 errors.
 *
 * Features:
 * ---------
 * - Proactive token refresh (refreshes 2 minutes before expiry)
 * - Background refresh interval
 * - Visibility change handling (refresh when tab becomes active)
 * - Network reconnection handling
 *
 * Usage:
 * ------
 * ```typescript
 * import { sessionManager } from 'core/auth';
 *
 * // Start session management (call once on app load)
 * sessionManager.start();
 *
 * // Stop when needed
 * sessionManager.stop();
 * ```
 * ============================================================================
 */

import * as tokenManager from './token-manager.service';
import { refreshToken as refreshTokenApi } from './auth.service';
import { SESSION_CONFIG } from './constants';

// Refresh 2 minutes before expiry for safety margin
const REFRESH_BUFFER_MS = 2 * 60 * 1000;

// Check interval: every 30 seconds
const CHECK_INTERVAL_MS = 30 * 1000;

let refreshInterval: ReturnType<typeof setInterval> | null = null;
let isRefreshing = false;

/**
 * Perform token refresh
 *
 * @returns Promise resolving to true if refresh succeeded
 */
const performRefresh = async (): Promise<boolean> => {
  if (isRefreshing) {
    console.log('[SessionManager] Refresh already in progress, skipping');
    return false;
  }

  const refreshToken = tokenManager.getRefreshToken();
  if (!refreshToken) {
    console.log('[SessionManager] No refresh token available');
    return false;
  }

  if (tokenManager.isTokenExpired(refreshToken)) {
    console.log('[SessionManager] Refresh token expired');
    return false;
  }

  try {
    isRefreshing = true;
    console.log('[SessionManager] Proactively refreshing tokens...');

    const response = await refreshTokenApi(refreshToken);

    const rememberMe = tokenManager.getRememberMe();
    tokenManager.setTokens(
      {
        accessToken: response.accessToken,
        refreshToken: response.refreshToken || refreshToken,
        expiresAt: response.expiresAt,
      },
      rememberMe
    );

    console.log('[SessionManager] Tokens refreshed successfully');
    return true;
  } catch (error) {
    console.error('[SessionManager] Token refresh failed:', error);
    return false;
  } finally {
    isRefreshing = false;
  }
};

/**
 * Check if tokens need refresh and refresh if needed
 */
const checkAndRefresh = async (): Promise<void> => {
  const accessToken = tokenManager.getAccessToken();

  if (!accessToken) {
    return;
  }

  const timeUntilExpiry = tokenManager.getTimeUntilExpiry(accessToken);

  // Refresh if within buffer time
  if (timeUntilExpiry > 0 && timeUntilExpiry <= REFRESH_BUFFER_MS) {
    console.log(
      `[SessionManager] Token expires in ${Math.round(timeUntilExpiry / 1000)}s, refreshing...`
    );
    await performRefresh();
  }
};

/**
 * Handle visibility change
 *
 * When tab becomes visible, check if tokens need refresh.
 */
const handleVisibilityChange = (): void => {
  if (document.visibilityState === 'visible') {
    console.log('[SessionManager] Tab became visible, checking tokens...');
    checkAndRefresh();
  }
};

/**
 * Handle online event
 *
 * When network reconnects, check if tokens need refresh.
 */
const handleOnline = (): void => {
  console.log('[SessionManager] Network reconnected, checking tokens...');
  checkAndRefresh();
};

/**
 * Start session management
 *
 * - Sets up periodic token refresh checks
 * - Listens for visibility changes
 * - Listens for network reconnection
 */
const start = (): void => {
  if (!SESSION_CONFIG.ENABLE_AUTO_REFRESH) {
    console.log('[SessionManager] Auto-refresh disabled in config');
    return;
  }

  // Stop any existing interval
  stop();

  console.log('[SessionManager] Starting session management...');

  // Initial check
  checkAndRefresh();

  // Set up periodic checks
  refreshInterval = setInterval(checkAndRefresh, CHECK_INTERVAL_MS);

  // Listen for visibility changes
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  // Listen for network reconnection
  if (typeof window !== 'undefined') {
    window.addEventListener('online', handleOnline);
  }
};

/**
 * Stop session management
 */
const stop = (): void => {
  console.log('[SessionManager] Stopping session management...');

  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }

  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  }

  if (typeof window !== 'undefined') {
    window.removeEventListener('online', handleOnline);
  }
};

/**
 * Force immediate refresh
 *
 * Use when you need to ensure tokens are fresh before a critical operation.
 */
const forceRefresh = async (): Promise<boolean> => {
  const accessToken = tokenManager.getAccessToken();

  if (!accessToken) {
    return false;
  }

  // If token is still valid and has plenty of time, skip
  const timeUntilExpiry = tokenManager.getTimeUntilExpiry(accessToken);
  if (timeUntilExpiry > REFRESH_BUFFER_MS * 2) {
    console.log('[SessionManager] Token still valid, skipping forced refresh');
    return true;
  }

  return performRefresh();
};

/**
 * Check if session is valid
 *
 * Returns true if we have valid tokens.
 */
const isSessionValid = (): boolean => {
  const accessToken = tokenManager.getAccessToken();
  const refreshToken = tokenManager.getRefreshToken();

  // Have valid access token
  if (accessToken && !tokenManager.isTokenExpired(accessToken)) {
    return true;
  }

  // Have valid refresh token (can get new access token)
  if (refreshToken && !tokenManager.isTokenExpired(refreshToken)) {
    return true;
  }

  return false;
};

/**
 * Session Manager
 *
 * Singleton-like object for session management.
 */
export const sessionManager = {
  start,
  stop,
  forceRefresh,
  isSessionValid,
  checkAndRefresh,
};

export default sessionManager;
